// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Enhanced Events Configuration
 * 
 * Separates immediate events from interactive events with proper chain handling
 */

import GameConfig from './GameConfig';

/**
 * IMMEDIATE EVENTS - Quick resolution, single modal interaction
 */
export const IMMEDIATE_EVENTS = {
  ASTEROID_FIELD: {
    type: 'ASTEROID_FIELD',
    category: 'minigame',
    title: '⚠️ ASTEROID FIELD DETECTED',
    message: 'Dense asteroid field ahead. Recommend evasive maneuvers or reduced speed.',
    color: '#ffaa00',
    actions: [
      {
        label: `EVADE (-${GameConfig.events.asteroidField.evadeFuelCost}% FUEL)`,
        feedback: '🎯 EVASIVE MANEUVERS - Successfully navigated through asteroid field',
        effect: (context) => {
          context.removeFuel(GameConfig.events.asteroidField.evadeFuelCost);
          if (context.isLowFuel()) {
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ FUEL CRITICAL: ${Math.round(context.fuel)}% remaining!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      },
      {
        label: 'SLOW DOWN (NO FUEL)',
        feedback: '🐌 SPEED REDUCED - Proceeding cautiously through debris',
        effect: (context) => {
          if (context.selectedWarpFactor > 1) {
            context.onSetWarpFactor(Math.max(1, context.selectedWarpFactor - 2));
          }
        }
      },
      {
        label: 'NAVIGATE MANUALLY (SKILL CHALLENGE)',
        feedback: '🎮 MANUAL NAVIGATION - Taking direct control of ship',
        triggersSystem: 'ASTEROID_MINIGAME',
        systemData: {
          difficulty: 'normal',
          duration: 45,
          asteroidDensity: 1.0
        }
      },
      {
        label: 'IGNORE (RISKY)',
        feedback: '💥 MINOR IMPACTS - Hull integrity holding, continuing at speed',
        effect: (context) => {
          if (Math.random() < GameConfig.events.asteroidField.damageChance) {
            const damageRange = GameConfig.events.asteroidField.maxDamage - GameConfig.events.asteroidField.minDamage + 1;
            const damage = Math.floor(Math.random() * damageRange) + GameConfig.events.asteroidField.minDamage;
            context.damageSystem('HULL', damage);
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ HULL DAMAGE: ${damage} HP from asteroid impacts!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      }
    ]
  },

  COMBAT_ENCOUNTER: {
    type: 'COMBAT_ENCOUNTER',
    category: 'minigame',
    title: '⚠️ HOSTILE SHIPS DETECTED',
    message: 'Multiple hostile contacts on sensors. Weapons systems standing by.',
    color: '#ff4444',
    actions: [
      {
        label: 'ENGAGE IN COMBAT (SKILL CHALLENGE)',
        feedback: '🎯 COMBAT INITIATED - Entering first-person targeting mode',
        triggersSystem: 'COMBAT_MINIGAME',
        systemData: {
          enemies: [
            { type: 'SCOUT', name: 'HOSTILE-ALPHA', shields: 50, hull: 50, speed: 1.5 },
            { type: 'INTERCEPTOR', name: 'HOSTILE-BETA', shields: 100, hull: 100, speed: 1.0 },
            { type: 'CRUISER', name: 'HOSTILE-GAMMA', shields: 200, hull: 200, speed: 0.5 }
          ]
        }
      },
      {
        label: `EVADE (-${GameConfig.events.asteroidField.evadeFuelCost}% FUEL)`,
        feedback: '🚀 EVASIVE MANEUVERS - Successfully escaped hostile contacts',
        effect: (context) => {
          context.removeFuel(GameConfig.events.asteroidField.evadeFuelCost);
        }
      },
      {
        label: 'ATTEMPT DIPLOMACY',
        feedback: '📡 HAILING FREQUENCIES OPEN - No response from hostile ships',
        effect: (context) => {
          if (Math.random() < 0.3) {
            setTimeout(() => {
              context.setActionFeedback({
                message: '✅ DIPLOMACY SUCCESSFUL - Hostiles standing down',
                color: '#00ff88'
              });
            }, 2000);
          } else {
            setTimeout(() => {
              context.setActionFeedback({
                message: '❌ DIPLOMACY FAILED - Hostiles opening fire!',
                color: '#ff4444'
              });
              const damage = Math.floor(Math.random() * 20) + 10;
              context.damageSystem('HULL', damage);
            }, 2000);
          }
        }
      }
    ]
  },

  NEBULA: {
    type: 'NEBULA',
    category: 'immediate',
    title: '🌌 NEBULA DETECTED',
    message: 'Beautiful nebula ahead. Sensors indicate unusual energy readings.',
    color: '#ff00ff',
    actions: [
      {
        label: `SCAN (-${GameConfig.events.nebula.scanFuelCost}% FUEL)`,
        feedback: '🔬 SCANNING COMPLETE - Fascinating data collected for science division',
        effect: (context) => {
          context.removeFuel(GameConfig.events.nebula.scanFuelCost);
          if (Math.random() < GameConfig.events.nebula.damageChance) {
            const damageRange = GameConfig.events.nebula.maxDamage - GameConfig.events.nebula.minDamage + 1;
            const damage = Math.floor(Math.random() * damageRange) + GameConfig.events.nebula.minDamage;
            context.damageSystem('SENSORS', damage);
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ SENSORS DAMAGED: ${damage} HP from nebula interference!`,
                color: '#ff4444'
              });
            }, 2000);
          } else if (context.isLowFuel()) {
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ FUEL CRITICAL: ${Math.round(context.fuel)}% remaining!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      },
      {
        label: `BYPASS (-${GameConfig.events.nebula.bypassFuelCost}% FUEL)`,
        feedback: '🔄 COURSE ADJUSTED - Routing around nebula perimeter',
        effect: (context) => {
          context.removeFuel(GameConfig.events.nebula.bypassFuelCost);
          if (context.isLowFuel()) {
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ FUEL CRITICAL: ${Math.round(context.fuel)}% remaining!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      }
    ]
  },

  ANOMALY: {
    type: 'ANOMALY',
    category: 'immediate',
    title: '⚡ SUBSPACE ANOMALY',
    message: 'Detecting subspace distortion. Could be dangerous or beneficial.',
    color: '#ffff00',
    actions: [
      {
        label: 'ANALYZE (±FUEL)',
        feedback: '🔮 ANALYSIS COMPLETE - ',
        effect: (context) => {
          const outcome = Math.random();
          if (outcome > (1 - GameConfig.events.anomalyAnalysis.energyBoostChance)) {
            const bonus = GameConfig.events.anomalyAnalysis.energyBoostCredits;
            context.addCredits(bonus);
            setTimeout(() => {
              context.setActionFeedback({
                message: `✨ ENERGY HARVEST: +${bonus} credits from anomaly!`,
                color: '#00ff88'
              });
            }, 2000);
          } else if (outcome > (1 - GameConfig.events.anomalyAnalysis.energyBoostChance - GameConfig.events.anomalyAnalysis.energyDrainChance)) {
            const drain = GameConfig.events.anomalyAnalysis.energyDrainFuel;
            context.removeFuel(drain);
            if (context.isLowFuel()) {
              setTimeout(() => {
                context.setActionFeedback({
                  message: `⚠️ FUEL CRITICAL: ${Math.round(context.fuel)}% remaining!`,
                  color: '#ff4444'
                });
              }, 2000);
            }
          } else {
            const systemDamaged = Math.random() > GameConfig.ui.anomalySystemSelection ? 'SENSORS' : 'POWER';
            const damageRange = GameConfig.events.anomalyInvestigation.systemDamageMax - GameConfig.events.anomalyInvestigation.systemDamageMin + 1;
            const damage = Math.floor(Math.random() * damageRange) + GameConfig.events.anomalyInvestigation.systemDamageMin;
            context.damageSystem(systemDamaged, damage);
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ ${systemDamaged} DAMAGED: ${damage} HP from subspace feedback!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      },
      {
        label: `AVOID (-${GameConfig.events.anomalyInvestigation.avoidFuelCost}% FUEL)`,
        feedback: '↩️ DETOUR INITIATED - Maintaining safe distance from anomaly',
        effect: (context) => {
          context.removeFuel(GameConfig.events.anomalyInvestigation.avoidFuelCost);
          if (context.isLowFuel()) {
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ FUEL CRITICAL: ${Math.round(context.fuel)}% remaining!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      }
    ]
  }
};

/**
 * INTERACTIVE EVENTS - Complex chains that spawn new events or trigger systems
 */
export const INTERACTIVE_EVENTS = {
  DISTRESS_SIGNAL: {
    type: 'DISTRESS_SIGNAL',
    category: 'interactive',
    title: '📡 DISTRESS SIGNAL',
    message: 'Receiving distress call from nearby vessel. They require immediate assistance.',
    color: '#00ffff',
    actions: [
      {
        label: `INVESTIGATE (-${GameConfig.events.distressSignal.investigateFuelCost}% FUEL)`,
        feedback: '🛸 COURSE REROUTED - Diverting to distress beacon coordinates',
        effect: (context) => {
          context.removeFuel(GameConfig.events.distressSignal.investigateFuelCost);
          if (context.isLowFuel()) {
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ FUEL CRITICAL: ${Math.round(context.fuel)}% remaining!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        },
        // This action spawns a new event after delay
        spawnsEvent: (context) => {
          return new Promise((resolve) => {
            setTimeout(() => {
              const outcome = Math.random();
              
              if (outcome < GameConfig.events.distressSignal.rescueChance) {
                resolve(DISTRESS_OUTCOMES.RESCUE_SUCCESS);
              } else if (outcome < GameConfig.events.distressSignal.rescueChance + GameConfig.events.distressSignal.trapChance) {
                resolve(DISTRESS_OUTCOMES.PIRATE_TRAP);
              } else {
                resolve(DISTRESS_OUTCOMES.SALVAGE_FOUND);
              }
            }, 3000);
          });
        }
      },
      {
        label: 'IGNORE (SAVE FUEL)',
        feedback: '📝 SIGNAL LOGGED - Continuing on current heading',
        effect: () => { /* Continue on course */ }
      }
    ]
  },

  BLACK_HOLE: {
    type: 'BLACK_HOLE',
    category: 'interactive',
    title: '🕳️ BLACK HOLE DETECTED',
    message: 'Massive gravitational anomaly ahead. Extreme caution advised.',
    color: '#ff4444',
    actions: [
      {
        label: `EMERGENCY SHUTDOWN (-${GameConfig.events.gravityWell.shutdownFuelCost}% FUEL)`,
        feedback: '🛑 EMERGENCY STOP - All systems shutting down to avoid gravitational capture',
        effect: (context) => {
          context.removeFuel(GameConfig.events.gravityWell.shutdownFuelCost);
        },
        // Spawns gravity well escape sequence
        spawnsEvent: (context) => {
          return Promise.resolve(BLACK_HOLE_OUTCOMES.ESCAPE_SEQUENCE);
        }
      },
      {
        label: `POWER THROUGH (-${GameConfig.events.gravityWell.powerThroughFuelCost}% FUEL)`,
        feedback: '⚡ MAXIMUM POWER - Attempting to break free of gravitational pull',
        effect: (context) => {
          context.removeFuel(GameConfig.events.gravityWell.powerThroughFuelCost);
        },
        spawnsEvent: (context) => {
          return Promise.resolve(BLACK_HOLE_OUTCOMES.POWER_STRUGGLE);
        }
      },
      {
        label: 'RIDE THE GRAVITY WELL (RISKY)',
        feedback: '🌀 GRAVITY ASSIST - Using black hole\'s pull for acceleration boost',
        spawnsEvent: (context) => {
          return Promise.resolve(BLACK_HOLE_OUTCOMES.GRAVITY_SLINGSHOT);
        }
      }
    ]
  },

  ALIEN_ENCOUNTER: {
    type: 'ALIEN_ENCOUNTER',
    title: '👽 ALIEN CONTACT',
    message: 'Unknown vessel detected. They\'re attempting to establish communication.',
    color: '#00ff88',
    actions: [
      {
        label: 'OPEN COMMUNICATIONS',
        feedback: '📡 HAILING FREQUENCIES OPEN - Establishing first contact protocols',
        spawnsEvent: (context) => {
          return new Promise((resolve) => {
            setTimeout(() => {
              const outcome = Math.random();
              if (outcome < 0.4) {
                resolve(ALIEN_OUTCOMES.PEACEFUL_TRADE);
              } else if (outcome < 0.7) {
                resolve(ALIEN_OUTCOMES.TECHNOLOGY_EXCHANGE);
              } else {
                resolve(ALIEN_OUTCOMES.HOSTILE_ENCOUNTER);
              }
            }, 3000);
          });
        }
      },
      {
        label: 'MAINTAIN DISTANCE',
        feedback: '🛡️ DEFENSIVE POSTURE - Monitoring alien vessel from safe distance',
        effect: () => { /* Continue with caution */ }
      },
      {
        label: 'RETREAT',
        feedback: '💨 TACTICAL WITHDRAWAL - Disengaging from unknown contact',
        effect: (context) => {
          context.removeFuel(5);
        }
      }
    ]
  },

  ALIEN_DISTRESS_CALL: {
    type: 'ALIEN_DISTRESS_CALL',
    category: 'interactive',
    title: '🆘 ALIEN DISTRESS SIGNAL',
    message: 'Receiving distress call from alien vessel. They report critical systems failure and request immediate assistance.',
    color: '#ff8800',
    actions: [
      {
        label: 'RENDER ASSISTANCE',
        feedback: '🚑 RESCUE OPERATION - Deploying aid to distressed vessel'
        // Will trigger alien modal via handler
      },
      {
        label: 'IGNORE SIGNAL',
        feedback: '📡 SIGNAL IGNORED - Continuing on current heading',
        effect: () => { /* Continue without helping */ }
      },
      {
        label: 'INVESTIGATE CAUTIOUSLY',
        feedback: '🔍 CAUTIOUS APPROACH - Scanning for potential threats'
        // Will trigger alien modal via handler
      }
    ]
  },

  ALIEN_FIRST_CONTACT: {
    type: 'ALIEN_FIRST_CONTACT',
    category: 'interactive',
    title: '🌟 FIRST CONTACT',
    message: 'Sensors detect an unknown alien species! This could be first contact with a new civilization.',
    color: '#00ffff',
    actions: [
      {
        label: 'INITIATE CONTACT',
        feedback: '📡 FIRST CONTACT PROTOCOLS - Establishing communication with unknown species'
        // Will trigger alien modal via handler
      },
      {
        label: 'OBSERVE FROM DISTANCE',
        feedback: '🔭 PASSIVE OBSERVATION - Gathering data without direct contact',
        effect: (context) => {
          context.addCredits(100);
          setTimeout(() => {
            context.setActionFeedback({
              message: '📊 SCIENTIFIC DATA: +100 credits for xenobiology research',
              color: '#00ffff'
            });
          }, 2000);
        }
      },
      {
        label: 'AVOID CONTACT',
        feedback: '↩️ COURSE CORRECTION - Maintaining distance from unknown species',
        effect: () => { /* Continue safely */ }
      }
    ]
  },

  ALIEN_AMBUSH: {
    type: 'ALIEN_AMBUSH',
    category: 'interactive',
    title: '⚠️ HOSTILE CONTACT',
    message: 'WARNING: Alien vessels decloaking! They\'re powering weapons and moving to intercept!',
    color: '#ff4444',
    actions: [
      {
        label: 'DEFENSIVE POSTURE',
        feedback: '🛡️ SHIELDS UP - Preparing for potential combat'
        // Will trigger alien modal via handler
      },
      {
        label: 'ATTEMPT COMMUNICATION',
        feedback: '📡 HAILING - Attempting to de-escalate situation'
        // Will trigger alien modal via handler
      },
      {
        label: 'EMERGENCY WARP',
        feedback: '💨 EMERGENCY ESCAPE - Maximum warp, now!',
        effect: (context) => {
          context.removeFuel(15);
          const escapeSuccess = Math.random() < 0.7;
          if (escapeSuccess) {
            setTimeout(() => {
              context.setActionFeedback({
                message: '✓ ESCAPED - Successfully evaded hostile vessels',
                color: '#00ff88'
              });
            }, 2000);
          } else {
            context.damageSystem('SHIELDS', 20);
            setTimeout(() => {
              context.setActionFeedback({
                message: '💥 PURSUIT DAMAGE: 20 HP shield damage during escape',
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      }
    ]
  },

  ALIEN_TRADE_CONVOY: {
    type: 'ALIEN_TRADE_CONVOY',
    category: 'interactive',
    title: '🚚 ALIEN TRADE CONVOY',
    message: 'Alien merchant convoy detected. They\'re broadcasting trade offers and safe passage guarantees.',
    color: '#00ff88',
    actions: [
      {
        label: 'APPROACH CONVOY',
        feedback: '💰 TRADE NEGOTIATIONS - Establishing contact with merchants'
        // Will trigger alien modal via handler
      },
      {
        label: 'REQUEST ESCORT',
        feedback: '🛡️ CONVOY ESCORT - Offering protection services'
        // Will trigger alien modal via handler
      },
      {
        label: 'CONTINUE PAST',
        feedback: '↩️ MAINTAINING COURSE - Declining trade opportunity',
        effect: () => { /* Continue without trading */ }
      }
    ]
  },

  ALIEN_BETRAYAL: {
    type: 'ALIEN_BETRAYAL',
    category: 'interactive',
    title: '🎭 SUSPICIOUS SIGNAL',
    message: 'Receiving friendly distress signal from alien vessel. They claim engine failure and request assistance... but something feels off.',
    color: '#ff8800',
    actions: [
      {
        label: 'HELP THEM',
        feedback: '🚑 RENDERING AID - Approaching distressed vessel'
        // Will trigger alien modal via handler - may be trap!
      },
      {
        label: 'SCAN FOR TRAPS',
        feedback: '🔍 DEEP SCAN - Analyzing vessel for anomalies',
        effect: (context) => {
          const isTrap = Math.random() < 0.6; // 60% chance it's a trap
          if (isTrap) {
            setTimeout(() => {
              context.setActionFeedback({
                message: '⚠️ TRAP DETECTED: Weapons signatures found! Avoiding ambush.',
                color: '#ff8800'
              });
            }, 2000);
          } else {
            context.addCredits(100);
            setTimeout(() => {
              context.setActionFeedback({
                message: '✓ LEGITIMATE DISTRESS: +100 credits for caution and verification',
                color: '#00ff88'
              });
            }, 2000);
          }
        }
      },
      {
        label: 'IGNORE SIGNAL',
        feedback: '📡 SIGNAL IGNORED - Better safe than sorry',
        effect: () => { /* Continue safely */ }
      }
    ]
  },

  CULTURAL_EXCHANGE: {
    type: 'CULTURAL_EXCHANGE',
    category: 'interactive',
    title: '🎨 CULTURAL EXCHANGE OFFER',
    message: 'Alien vessel broadcasting cultural exchange invitation. They wish to share art, music, and knowledge of their civilization.',
    color: '#00ffff',
    actions: [
      {
        label: 'PARTICIPATE',
        feedback: '🎭 CULTURAL EXCHANGE - Sharing knowledge and traditions'
        // Will trigger alien modal via handler
      },
      {
        label: 'OBSERVE ONLY',
        feedback: '👀 PASSIVE OBSERVATION - Recording cultural data',
        effect: (context) => {
          context.addCredits(50);
          setTimeout(() => {
            context.setActionFeedback({
              message: '📊 CULTURAL DATA: +50 credits for xenoanthropology research',
              color: '#00ffff'
            });
          }, 2000);
        }
      },
      {
        label: 'DECLINE POLITELY',
        feedback: '🙏 RESPECTFUL DECLINE - Continuing on current mission',
        effect: () => { /* Continue without participating */ }
      }
    ]
  },

  TERRITORIAL_DISPUTE: {
    type: 'TERRITORIAL_DISPUTE',
    category: 'interactive',
    title: '⚖️ TERRITORIAL DISPUTE',
    message: 'Two alien factions are in heated conflict over this sector. Both are requesting your support as a neutral third party.',
    color: '#ffaa00',
    actions: [
      {
        label: 'MEDIATE CONFLICT',
        feedback: '⚖️ DIPLOMATIC MEDIATION - Attempting to broker peace'
        // Will trigger special mediation modal
      },
      {
        label: 'SIDE WITH FACTION A',
        feedback: '🤝 ALLIANCE FORMED - Supporting Faction A\'s claim'
        // Will trigger alien modal for Faction A
      },
      {
        label: 'SIDE WITH FACTION B',
        feedback: '🤝 ALLIANCE FORMED - Supporting Faction B\'s claim'
        // Will trigger alien modal for Faction B
      },
      {
        label: 'REMAIN NEUTRAL',
        feedback: '🏳️ NEUTRAL STANCE - Refusing to take sides',
        effect: (context) => {
          setTimeout(() => {
            context.setActionFeedback({
              message: '⚖️ NEUTRALITY MAINTAINED: Both factions respect your impartiality',
              color: '#00ffff'
            });
          }, 2000);
        }
      }
    ]
  },

  ESCORT_MISSION: {
    type: 'ESCORT_MISSION',
    category: 'interactive',
    title: '🛡️ ESCORT REQUEST',
    message: 'Alien convoy requests armed escort through dangerous territory. They offer generous compensation for protection.',
    color: '#00ff88',
    actions: [
      {
        label: 'ACCEPT ESCORT',
        feedback: '🛡️ ESCORT DUTY - Providing armed protection',
        effect: (context) => {
          const success = Math.random() < 0.7; // 70% success rate
          if (success) {
            context.addCredits(400);
            setTimeout(() => {
              context.setActionFeedback({
                message: '✓ ESCORT SUCCESS: +400 credits! Convoy arrived safely.',
                color: '#00ff88'
              });
            }, 3000);
          } else {
            context.damageSystem('SHIELDS', 25);
            context.addCredits(200);
            setTimeout(() => {
              context.setActionFeedback({
                message: '⚠️ ESCORT ATTACKED: 25 HP damage, +200 credits (partial payment)',
                color: '#ff8800'
              });
            }, 3000);
          }
        }
      },
      {
        label: 'NEGOTIATE PRICE',
        feedback: '💰 PRICE NEGOTIATION - Requesting higher compensation',
        effect: (context) => {
          const negotiationSuccess = Math.random() < 0.5;
          if (negotiationSuccess) {
            context.addCredits(600);
            setTimeout(() => {
              context.setActionFeedback({
                message: '✓ NEGOTIATION SUCCESS: +600 credits! Premium escort rate secured.',
                color: '#00ff88'
              });
            }, 3000);
          } else {
            setTimeout(() => {
              context.setActionFeedback({
                message: '✗ NEGOTIATION FAILED: Convoy found another escort',
                color: '#ff8800'
              });
            }, 2000);
          }
        }
      },
      {
        label: 'DECLINE MISSION',
        feedback: '↩️ MISSION DECLINED - Too risky for current situation',
        effect: () => { /* Continue without escorting */ }
      }
    ]
  },

  DERELICT_SHIP: {
    type: 'DERELICT_SHIP',
    title: '🚢 DERELICT VESSEL',
    message: 'Abandoned ship detected. No life signs, but sensors indicate intact systems.',
    color: '#ffaa00',
    actions: [
      {
        label: 'BOARD AND INVESTIGATE',
        feedback: '🔍 BOARDING PARTY - Preparing to investigate the derelict vessel',
        spawnsEvent: (context) => {
          return new Promise((resolve) => {
            setTimeout(() => {
              const outcome = Math.random();
              if (outcome < 0.3) {
                resolve(DERELICT_OUTCOMES.VALUABLE_SALVAGE);
              } else if (outcome < 0.6) {
                resolve(DERELICT_OUTCOMES.SHIP_TRAP);
              } else {
                resolve(DERELICT_OUTCOMES.GHOST_SHIP);
              }
            }, 4000);
          });
        }
      },
      {
        label: 'SCAN FROM DISTANCE',
        feedback: '🔬 LONG RANGE SCAN - Analyzing vessel composition and cargo',
        effect: (context) => {
          context.removeFuel(3);
          const scanResult = Math.random();
          if (scanResult < 0.5) {
            context.addCredits(75);
            setTimeout(() => {
              context.setActionFeedback({
                message: '💰 SCAN REWARD: +75 credits for stellar cartography data',
                color: '#00ff88'
              });
            }, 2000);
          }
        }
      },
      {
        label: 'AVOID',
        feedback: '↩️ COURSE CORRECTION - Maintaining safe distance from derelict',
        effect: () => { /* Continue safely */ }
      }
    ]
  },

  QUANTUM_STORM: {
    type: 'QUANTUM_STORM',
    title: '⚡ QUANTUM STORM',
    message: 'Massive subspace distortion ahead. Quantum fluctuations are off the charts.',
    color: '#ff00ff',
    actions: [
      {
        label: 'RIDE THE STORM',
        feedback: '🌪️ STORM SURFING - Using quantum currents for acceleration boost',
        spawnsEvent: (context) => {
          return Promise.resolve(QUANTUM_OUTCOMES.STORM_RIDE);
        }
      },
      {
        label: 'EMERGENCY STOP',
        feedback: '🛑 FULL STOP - Emergency shutdown to avoid quantum interference',
        effect: (context) => {
          context.removeFuel(15);
          if (context.isLowFuel()) {
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ FUEL CRITICAL: ${Math.round(context.fuel)}% remaining!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      },
      {
        label: 'POWER THROUGH',
        feedback: '⚡ MAXIMUM POWER - Attempting to punch through the storm',
        spawnsEvent: (context) => {
          return Promise.resolve(QUANTUM_OUTCOMES.POWER_THROUGH);
        }
      }
    ]
  }
};

/**
 * OUTCOME EVENTS - Generated by interactive events
 */
export const DISTRESS_OUTCOMES = {
  RESCUE_SUCCESS: {
    type: 'RESCUE_SUCCESS',
    title: '✅ RESCUE SUCCESSFUL',
    message: 'Crew rescued from damaged freighter. They\'re grateful and offer payment as thanks.',
    color: '#00ff88',
    actions: [
      {
        label: `ACCEPT PAYMENT (+${GameConfig.events.distressSignal.rescueReward} CREDITS)`,
        feedback: '💰 PAYMENT RECEIVED - Credits transferred to your account',
        effect: (context) => {
          context.addCredits(GameConfig.events.distressSignal.rescueReward);
        }
      }
    ]
  },

  PIRATE_TRAP: {
    type: 'PIRATE_TRAP',
    title: '☠️ IT\'S A TRAP!',
    message: 'The distress signal was fake! Pirates decloak and open fire!',
    color: '#ff4444',
    actions: [
      {
        label: 'FIGHT',
        feedback: '⚔️ BATTLE STATIONS - Engaging hostile contacts',
        triggersSystem: 'COMBAT_SYSTEM',
        systemData: {
          enemyType: 'pirates',
          enemyCount: Math.floor(Math.random() * 2) + 2, // 2-3 pirates
          ambush: true,
          trap: true
        }
      }
    ]
  },

  SALVAGE_FOUND: {
    type: 'SALVAGE_FOUND',
    title: '🔍 ABANDONED VESSEL',
    message: 'The ship is abandoned but intact. Sensors detect valuable cargo and supplies.',
    color: '#ffaa00',
    actions: [
      {
        label: `SALVAGE CARGO (+${GameConfig.events.distressSignal.salvageReward} CREDITS)`,
        feedback: '💰 SALVAGE COMPLETE - Valuable cargo secured',
        effect: (context) => {
          context.addCredits(GameConfig.events.distressSignal.salvageReward);
        }
      },
      {
        label: 'LEAVE IT',
        feedback: '➡️ CONTINUING JOURNEY - Salvage left behind',
        effect: () => { /* Continue */ }
      }
    ]
  }
};

export const BLACK_HOLE_OUTCOMES = {
  ESCAPE_SEQUENCE: {
    type: 'ESCAPE_SEQUENCE',
    title: '🚀 ESCAPE ATTEMPT',
    message: 'Emergency thrusters firing. Will you make it out of the gravity well?',
    color: '#ffaa00',
    actions: [
      {
        label: 'FULL REVERSE',
        feedback: '🔄 REVERSING - Slowly pulling away from the event horizon',
        effect: (context) => {
          // Success based on engine health
          const engineHealth = context.getSystemHealth('ENGINES');
          if (engineHealth > 50) {
            setTimeout(() => {
              context.setActionFeedback({
                message: '✅ ESCAPE SUCCESSFUL - Clear of gravitational influence',
                color: '#00ff88'
              });
            }, 2000);
          } else {
            const damage = Math.floor(Math.random() * 15) + 10;
            context.damageSystem('ENGINES', damage);
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ ENGINE STRAIN: ${damage} HP damage from gravitational stress!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      }
    ]
  },

  POWER_STRUGGLE: {
    type: 'POWER_STRUGGLE',
    title: '⚡ POWER STRUGGLE',
    message: 'Engines at maximum! The ship is straining against the black hole\'s pull.',
    color: '#ff4444',
    actions: [
      {
        label: 'OVERLOAD ENGINES',
        feedback: '💥 OVERLOADING - Pushing engines beyond safe limits',
        effect: (context) => {
          // High risk, high reward
          if (Math.random() < 0.6) {
            // Success but with damage
            const damage = Math.floor(Math.random() * 20) + 15;
            context.damageSystem('ENGINES', damage);
            setTimeout(() => {
              context.setActionFeedback({
                message: `🚀 BREAKTHROUGH! Engine damage: ${damage} HP`,
                color: '#ffaa00'
              });
            }, 2000);
          } else {
            // Catastrophic failure
            context.damageSystem('ENGINES', 40);
            context.damageSystem('POWER', 25);
            setTimeout(() => {
              context.setActionFeedback({
                message: '💥 CATASTROPHIC FAILURE - Multiple systems damaged!',
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      }
    ]
  },

  GRAVITY_SLINGSHOT: {
    type: 'GRAVITY_SLINGSHOT',
    title: '🌀 GRAVITY SLINGSHOT',
    message: 'Using the black hole\'s gravity for a speed boost. This is either brilliant or insane.',
    color: '#00ffff',
    actions: [
      {
        label: 'EXECUTE MANEUVER',
        feedback: '🎯 SLINGSHOT MANEUVER - Threading the needle around the event horizon',
        effect: (context) => {
          // Very risky but can provide benefits
          const outcome = Math.random();
          if (outcome < 0.3) {
            // Great success - speed boost
            context.onSetWarpFactor(Math.min(9, context.selectedWarpFactor + 2));
            setTimeout(() => {
              context.setActionFeedback({
                message: '🚀 INCREDIBLE SUCCESS - Warp factor increased!',
                color: '#00ff88'
              });
            }, 2000);
          } else if (outcome < 0.7) {
            // Moderate success with minor damage
            const damage = Math.floor(Math.random() * 10) + 5;
            context.damageSystem('HULL', damage);
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ HULL STRESS: ${damage} HP damage, but we made it through!`,
                color: '#ffaa00'
              });
            }, 2000);
          } else {
            // Failure - significant damage
            context.damageSystem('HULL', 25);
            context.damageSystem('SHIELDS', 20);
            setTimeout(() => {
              context.setActionFeedback({
                message: '💥 TOO CLOSE - Severe damage from tidal forces!',
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      }
    ]
  }
};

export const PIRATE_OUTCOMES = {
  PURSUIT: {
    type: 'PURSUIT',
    title: '🚨 PIRATES IN PURSUIT',
    message: 'They\'re faster than expected! Pirates are closing in despite your head start.',
    color: '#ff4444',
    actions: [
      {
        label: 'PREPARE FOR COMBAT',
        feedback: '⚔️ BATTLE STATIONS - They\'ve caught up, time to fight',
        triggersSystem: 'COMBAT_SYSTEM',
        systemData: {
          enemyType: 'pirates',
          enemyCount: Math.floor(Math.random() * 2) + 2,
          pursuit: true
        }
      }
    ]
  },

  CLEAN_ESCAPE: {
    type: 'CLEAN_ESCAPE',
    title: '✅ ESCAPE SUCCESSFUL',
    message: 'Your quick thinking paid off. The pirates are left behind in your warp trail.',
    color: '#00ff88',
    actions: [
      {
        label: 'CONTINUE JOURNEY',
        feedback: '➡️ RESUMING COURSE - Crisis averted',
        effect: () => { /* Continue */ }
      }
    ]
  },

  SURRENDER: {
    type: 'SURRENDER',
    title: '🏳️ PIRATE BOARDING',
    message: 'Pirates board your ship. They\'re surprisingly professional about their robbery.',
    color: '#ffaa00',
    actions: [
      {
        label: 'COMPLY',
        feedback: '💰 CREDITS LOST - Pirates take 30% of your credits but leave you alive',
        effect: (context) => {
          const creditsLost = Math.floor(context.credits * 0.3);
          context.removeCredits(creditsLost);
          setTimeout(() => {
            context.setActionFeedback({
              message: `💸 ROBBERY COMPLETE - Lost ${creditsLost} credits`,
              color: '#ff4444'
            });
          }, 2000);
        }
      }
    ]
  }
};

export const ALIEN_OUTCOMES = {
  PEACEFUL_TRADE: {
    type: 'PEACEFUL_TRADE',
    title: '🤝 PEACEFUL CONTACT',
    message: 'The aliens are friendly traders! They offer exotic goods and technology.',
    color: '#00ff88',
    actions: [
      {
        label: 'TRADE (+200 CREDITS)',
        feedback: '💰 TRADE COMPLETE - Exotic materials exchanged for credits',
        effect: (context) => {
          context.addCredits(200);
        }
      },
      {
        label: 'EXCHANGE STAR CHARTS',
        feedback: '🗺️ KNOWLEDGE SHARED - New navigation data acquired',
        effect: (context) => {
          context.addCredits(100);
          setTimeout(() => {
            context.setActionFeedback({
              message: '📊 NAVIGATION BONUS: +100 credits for stellar data',
              color: '#00ff88'
            });
          }, 2000);
        }
      }
    ]
  },

  TECHNOLOGY_EXCHANGE: {
    type: 'TECHNOLOGY_EXCHANGE',
    title: '🔬 TECHNOLOGY EXCHANGE',
    message: 'The aliens are scientists! They offer to upgrade your ship systems.',
    color: '#00ffff',
    actions: [
      {
        label: 'ACCEPT UPGRADE',
        feedback: '⚡ SYSTEMS ENHANCED - Alien technology integrated',
        effect: (context) => {
          // Repair random system to full
          const systems = ['HULL', 'SHIELDS', 'ENGINES', 'WEAPONS', 'SENSORS', 'POWER'];
          const randomSystem = systems[Math.floor(Math.random() * systems.length)];
          context.damageSystem(randomSystem, -50); // Negative damage = repair
          setTimeout(() => {
            context.setActionFeedback({
              message: `🔧 ${randomSystem} ENHANCED - Alien technology boost!`,
              color: '#00ffff'
            });
          }, 2000);
        }
      }
    ]
  },

  HOSTILE_ENCOUNTER: {
    type: 'HOSTILE_ENCOUNTER',
    title: '👽 HOSTILE ALIENS',
    message: 'These aliens are not friendly! They view your presence as a threat.',
    color: '#ff4444',
    actions: [
      {
        label: 'DEFEND YOURSELF',
        feedback: '⚔️ BATTLE STATIONS - Engaging alien hostiles',
        triggersSystem: 'COMBAT_SYSTEM',
        systemData: {
          enemyType: 'aliens',
          enemyCount: Math.floor(Math.random() * 2) + 1,
          hostile: true
        }
      }
    ]
  }
};

export const DERELICT_OUTCOMES = {
  VALUABLE_SALVAGE: {
    type: 'VALUABLE_SALVAGE',
    title: '💎 VALUABLE SALVAGE',
    message: 'The derelict contains rare materials and intact technology!',
    color: '#00ff88',
    actions: [
      {
        label: 'SALVAGE EVERYTHING (+350 CREDITS)',
        feedback: '💰 SALVAGE COMPLETE - Rare materials and tech components secured',
        effect: (context) => {
          context.addCredits(350);
        }
      }
    ]
  },

  SHIP_TRAP: {
    type: 'SHIP_TRAP',
    title: '⚠️ AUTOMATED DEFENSES',
    message: 'The ship isn\'t as abandoned as it seemed! Defense systems are activating!',
    color: '#ff4444',
    actions: [
      {
        label: 'FIGHT THE DRONES',
        feedback: '🤖 COMBAT DRONES - Engaging automated defense systems',
        triggersSystem: 'COMBAT_SYSTEM',
        systemData: {
          enemyType: 'drones',
          enemyCount: Math.floor(Math.random() * 3) + 2,
          automated: true
        }
      },
      {
        label: 'EMERGENCY EVACUATION',
        feedback: '💨 EVACUATING - Getting out before more systems come online',
        effect: (context) => {
          const damage = Math.floor(Math.random() * 10) + 5;
          context.damageSystem('SHIELDS', damage);
          setTimeout(() => {
            context.setActionFeedback({
              message: `⚠️ SHIELDS DAMAGED: ${damage} HP from defense systems!`,
              color: '#ff4444'
            });
          }, 2000);
        }
      }
    ]
  },

  GHOST_SHIP: {
    type: 'GHOST_SHIP',
    title: '👻 GHOST SHIP',
    message: 'Strange energy readings... This ship has been drifting for centuries.',
    color: '#ff00ff',
    actions: [
      {
        label: 'INVESTIGATE FURTHER',
        feedback: '🔍 DEEP SCAN - Analyzing ancient technology and energy signatures',
        effect: (context) => {
          const outcome = Math.random();
          if (outcome < 0.5) {
            // Ancient tech bonus
            context.addCredits(500);
            setTimeout(() => {
              context.setActionFeedback({
                message: '🏛️ ANCIENT TECH: +500 credits for archaeological discovery!',
                color: '#ff00ff'
              });
            }, 2000);
          } else {
            // Cursed ship - minor system damage
            const damage = Math.floor(Math.random() * 8) + 3;
            context.damageSystem('SENSORS', damage);
            setTimeout(() => {
              context.setActionFeedback({
                message: `👻 SPECTRAL INTERFERENCE: ${damage} HP sensor damage`,
                color: '#ff00ff'
              });
            }, 2000);
          }
        }
      },
      {
        label: 'LEAVE IMMEDIATELY',
        feedback: '💨 TACTICAL RETREAT - Some mysteries are better left unsolved',
        effect: () => { /* Escape safely */ }
      }
    ]
  }
};

export const QUANTUM_OUTCOMES = {
  STORM_RIDE: {
    type: 'STORM_RIDE',
    title: '🌪️ QUANTUM SURFING',
    message: 'You\'re riding the quantum currents! Reality bends around your ship.',
    color: '#ff00ff',
    actions: [
      {
        label: 'MAINTAIN COURSE',
        feedback: '🚀 QUANTUM BOOST - Surfing the storm currents',
        effect: (context) => {
          const outcome = Math.random();
          if (outcome < 0.4) {
            // Great success - warp boost
            context.onSetWarpFactor(Math.min(9, context.selectedWarpFactor + 1));
            setTimeout(() => {
              context.setActionFeedback({
                message: '⚡ QUANTUM ACCELERATION - Warp factor increased!',
                color: '#ff00ff'
              });
            }, 2000);
          } else if (outcome < 0.8) {
            // Moderate success - fuel efficiency
            context.addCredits(150);
            setTimeout(() => {
              context.setActionFeedback({
                message: '💎 QUANTUM CRYSTALS - +150 credits from exotic matter!',
                color: '#ff00ff'
              });
            }, 2000);
          } else {
            // Dangerous - system strain
            const damage = Math.floor(Math.random() * 15) + 10;
            context.damageSystem('ENGINES', damage);
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ QUANTUM STRAIN: ${damage} HP engine damage from reality distortion!`,
                color: '#ff4444'
              });
            }, 2000);
          }
        }
      }
    ]
  },

  POWER_THROUGH: {
    type: 'POWER_THROUGH',
    title: '⚡ BRUTE FORCE',
    message: 'Maximum power to all systems! You\'re punching through the quantum barrier.',
    color: '#ffaa00',
    actions: [
      {
        label: 'FULL POWER',
        feedback: '💥 BREAKTHROUGH - Forcing passage through quantum distortion',
        effect: (context) => {
          // High risk, high fuel cost, but guaranteed passage
          context.removeFuel(25);
          const damage = Math.floor(Math.random() * 20) + 10;
          context.damageSystem('POWER', damage);
          
          if (context.isLowFuel()) {
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚠️ FUEL CRITICAL: ${Math.round(context.fuel)}% remaining!`,
                color: '#ff4444'
              });
            }, 2000);
          } else {
            setTimeout(() => {
              context.setActionFeedback({
                message: `⚡ POWER STRAIN: ${damage} HP power system damage`,
                color: '#ffaa00'
              });
            }, 2000);
          }
        }
      }
    ]
  }
};